import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Axios from '@/axiosPool';
import useForm from '@/hooks/useForm';
import { getSuggested } from '@/util/email';
import { EMAIL_SUFFIX } from '@/components/project/Config/Config';
import { showToast } from '@/globalFunction';
import { saveCompanyForm } from '@/components/project/Company/components/AddCompany/Config';
import {
  LeadOption,
  canSubmit,
  buildCreateParams,
  CreateAccountState,
} from './createAccount.helpers';
import { replaceLocation } from './navigate';

interface UseCreateAccountFormArgs {
  tab: 'companies' | 'projects';
  onClose: () => void;
}

export const useCreateAccountForm = ({
  tab,
  onClose,
}: UseCreateAccountFormArgs) => {
  const dispatch = useDispatch();
  const loggedUser = useSelector((s: any) => s.loggedUser?.data);
  const isCompany = tab === 'companies';

  const { registerFormInput, resetForm, setFormFieldValue } = useForm({
    validationRules: saveCompanyForm.email.validationRules,
  });
  const email = registerFormInput('email');

  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [leadOption, setLeadOption] = useState<LeadOption | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const [typeId, setTypeId] = useState<number | null>(null);
  const [leadOptions, setLeadOptions] = useState<LeadOption[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Private projects (typeId 3) lock the lead to the logged-in user (legacy parity).
  useEffect(() => {
    if (!isCompany && typeId === 3 && loggedUser) {
      setLeadOption({
        value: loggedUser.id,
        label: `${loggedUser.firstName} ${loggedUser.lastName}`,
      });
    }
  }, [isCompany, typeId, loggedUser]);

  // Auto-suggest the email local part from the name (mirrors AddCompany).
  useEffect(() => {
    if (!nameTouched) return;
    const suggested = getSuggested(name);
    setFormFieldValue('email', suggested);
    if (name) {
      email.onValidate({ target: { name: 'email', value: suggested } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, nameTouched]);

  const onNameChange = useCallback((value: string) => {
    setName(value);
    setNameTouched(true);
  }, []);

  const openLeads = useCallback(async () => {
    setLeadsLoading(true);
    const res = await Axios.get('api/user', { isDeleted: 0, type: 'teamLead' });
    if (res?.data?.result) {
      setLeadOptions(
        res.data.result.map((u: any) => ({
          value: u.id,
          label: `${u.firstName} ${u.lastName}`,
        }))
      );
    }
    setLeadsLoading(false);
  }, []);

  const state: CreateAccountState = {
    isCompany,
    name,
    leadOption,
    color,
    typeId,
    emailValid: Boolean(email.valid),
    emailValue: email.value,
  };
  const fullEmail = `${email.value}@${EMAIL_SUFFIX}`;

  const submit = useCallback(async () => {
    const params = buildCreateParams(state, loggedUser.id, fullEmail);
    dispatch({ type: 'SET_SCREEN_LOADER', Loading: true });
    try {
      const res = await Axios.post('api/project', params, { timeout: 10000 });
      if (res) {
        replaceLocation(`/account#/projects/${res.data.project.id}/?new=1`);
        dispatch({ type: 'SET_PROJECT_NEWLY_ADDED', boolean: true });
        onClose();
      }
    } catch (e) {
      console.error(e);
      showToast('error', 'Something went wrong please try again later.');
    } finally {
      dispatch({ type: 'SET_SCREEN_LOADER', Loading: false });
    }
  }, [state, fullEmail, loggedUser, dispatch, onClose]);

  const reset = useCallback(() => {
    setName('');
    setNameTouched(false);
    setLeadOption(null);
    setColor(null);
    setTypeId(null);
    setConfirming(false);
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isCompany,
    name,
    onNameChange,
    setName,
    leadOption,
    setLeadOption,
    leadOptions,
    leadsLoading,
    openLeads,
    color,
    setColor,
    typeId,
    setTypeId,
    email,
    fullEmail,
    emailSuffix: EMAIL_SUFFIX,
    confirming,
    setConfirming,
    canSubmit: canSubmit(state),
    submit,
    reset,
  };
};
